import {Component, Inject, OnInit} from '@angular/core';
import {AuthService} from '@app/auth/services';
import * as fromAuthStore from 'src/app/auth/store';
import {ActionsSubject, Store} from '@ngrx/store';
import {
  Router,
  NavigationEnd,
  RouterState,
  ActivatedRoute,
} from '@angular/router';
import {AuthActionTypes} from 'src/app/auth/store';
import {ofType} from '@ngrx/effects';
import {Subject, takeUntil} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {CoreService} from './services';
import {DOCUMENT} from '@angular/common';
import {
  CLIENT_TOKEN,
  LANG_KEY,
  ROUTES,
  SELECTED_CLIENT,
  SELECTED_CLIENT_ID,
  SELECTED_MANAGER_ID,
} from 'src/utils/constants';
import {Title} from '@angular/platform-browser';
import {LanguageCode} from 'src/utils/enums';
import {cleanStorage, getPageRoute, isNewPersonalAreaPage} from 'src/utils/helpers';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  loginSubscription$ = new Subject<boolean>();
  backurl: string;
  pageRoute = getPageRoute();

  constructor(
    private authStore: Store<fromAuthStore.State>,
    private authService: AuthService,
    private router: Router,
    private actionsSubject: ActionsSubject,
    private translate: TranslateService,
    private coreService: CoreService,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document
  ) {

    let locale = this.coreService.getLocale();
    if (!locale) {
      this.coreService.setLocale(LanguageCode.UK);
    }
    translate.setDefaultLang(LanguageCode.UK);
    translate.use(locale);

    // TODO remove backurl ?
    //backurl handling
    if (window.location.href.includes('backurl')) {
      this.backurl =
        'https://' + window.location.href.split('?')[1].replace('backurl=', '');
    }

    //auth handling
    if (this.authService.isLoggedIn()) {
      if (this.backurl) {
        window.location.href = this.backurl;
      }
      if (!isNewPersonalAreaPage(this.pageRoute)) {
        this.authStore.dispatch(new fromAuthStore.GetRoles());
        this.authStore.dispatch(new fromAuthStore.GetCurrentUser());
      }
    } else {
      this.actionsSubject
        .pipe(ofType(AuthActionTypes.LOGIN_SUCCESS))
        .pipe(takeUntil(this.loginSubscription$))
        .subscribe((data: any) => {
          if (this.backurl) {
            window.location.href = this.backurl;
          }
          this.authStore.dispatch(new fromAuthStore.GetRoles());
          this.authStore.dispatch(new fromAuthStore.GetCurrentUser());
          this.loginSubscription$.next(true);
          this.loginSubscription$.complete();
        });
    }
  }

  updatePageTitle() {
    const title =
      this.translate.instant(
        this.getTitle(this.router.routerState, this.router.routerState.root).at(
          0
        ) || 'COMMON.KRONAS'
      ) || 'Kronas';

    this.titleService.setTitle(title);

    // You might need to define the 'event' object before using it in the gtag function
    const event = this.router.getCurrentNavigation();

    gtag('event', 'page_view', {
      page_title: title,
      page_path: event ? event.finalUrl : '',
      page_location: this.document.location.href,
    });
  }

  handleRouteEvents() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (!event.url.includes(ROUTES.PROJECTS.MY)) {
          cleanStorage(
            [
              CLIENT_TOKEN,
              SELECTED_CLIENT_ID,
              SELECTED_CLIENT,
              SELECTED_MANAGER_ID,
            ],
            'sessionStorage'
          );

          const token = window.sessionStorage.getItem(CLIENT_TOKEN);

          if (token) {
            this.authStore.dispatch(new fromAuthStore.Logout(token));
          }
        }
        this.updatePageTitle();
      }
    });
  }

  getTitle(state: RouterState, parent: ActivatedRoute): string[] {
    const data = [];

    if (parent && parent.snapshot.data && parent.snapshot.data['title']) {
      data.push(parent.snapshot.data['title']);
    }
    if (state && parent && parent.firstChild) {
      data.push(...this.getTitle(state, parent.firstChild));
    }
    return data;
  }

  ngOnInit() {
    const lang = window.localStorage.getItem(LANG_KEY) || LanguageCode.UK;
    this.document.documentElement.lang = lang;
  }

  ngAfterViewInit(): void {
    this.handleRouteEvents();

    this.translate.onLangChange.subscribe((response) => {
      this.document.documentElement.lang = response.lang;
      this.updatePageTitle();
    });
  }

  displayBlock() {
    return !this.router.url.includes(ROUTES.AUTH.AUTH) && !isNewPersonalAreaPage(this.pageRoute);
  }
}
